import { createFileRoute } from '@tanstack/react-router'
import { ArrowLeftIcon } from "@radix-ui/react-icons";
import { Link, useSearch } from "@tanstack/react-router";
import { AlertCircle, Calendar, Mail } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

enum AuthError {
  signupDisabled = "signup_disabled",
}

export const Route = createFileRoute('/auth-error')({
  component: AuthErrorPage,
})


function AuthErrorPage() {
  const { t } = useTranslation();
  const search = useSearch({ strict: false }) as { error?: string };
  const error = search.error;

  return (
    <div className="flex min-h-screen w-full bg-gradient-to-br from-slate-50 to-slate-100">
      <main className="flex-1 relative w-full h-screen overflow-auto">
        {error === AuthError.signupDisabled && (
          <div className="h-full py-12 px-4 sm:px-6 lg:px-8">
            <div className="w-full max-w-3xl mx-auto space-y-4">
              <Button asChild variant="ghost">
                <Link to="/">
                  <ArrowLeftIcon className="mr-2" />
                  {t("http_errors.back_to_home")}
                </Link>
              </Button>
              <Card className="border-0">
                <CardHeader className="space-y-3 pb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-red-100 rounded-full">
                      <AlertCircle className="w-6 h-6 text-red-600" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-slate-900">
                      {t("http_errors.auth_error.signup_disabled.title")}
                    </CardTitle>
                  </div>
                  <p className="text-slate-600 text-base leading-relaxed">
                    {t("http_errors.auth_error.signup_disabled.description")}
                  </p>
                </CardHeader>

                <CardContent className="space-y-6">
                  {/* Subtitle */}
                  <h3 className="text-lg font-semibold text-slate-900">
                    {t("http_errors.auth_error.signup_disabled.subtitle")}
                  </h3>

                  {/* Solutions List */}
                  <div className="space-y-5">
                    <div className="flex gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full font-semibold text-sm">
                        1
                      </div>
                      <div className="flex-1 space-y-1">
                        <h4 className="font-semibold text-slate-900">
                          {t(
                            "http_errors.auth_error.signup_disabled.user_not_invited",
                          )}
                        </h4>
                        <p className="text-slate-600 text-sm leading-relaxed">
                          {t(
                            "http_errors.auth_error.signup_disabled.user_not_invited.description",
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full font-semibold text-sm">
                        2
                      </div>
                      <div className="flex-1 space-y-1">
                        <h4 className="font-semibold text-slate-900">
                          {t(
                            "http_errors.auth_error.signup_disabled.wrong_account",
                          )}
                        </h4>
                        <p className="text-slate-600 text-sm leading-relaxed">
                          {t(
                            "http_errors.auth_error.signup_disabled.wrong_account.description",
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex gap-4 p-4 bg-slate-50 rounded-lg border border-slate-200">
                      <div className="flex-shrink-0 w-8 h-8 flex items-center justify-center bg-blue-100 text-blue-600 rounded-full font-semibold text-sm">
                        3
                      </div>
                      <div className="flex-1 space-y-1">
                        <h4 className="font-semibold text-slate-900">
                          {t(
                            "http_errors.auth_error.signup_disabled.company_not_using_tabula",
                          )}
                        </h4>
                        <p className="text-slate-600 text-sm leading-relaxed">
                          {t(
                            "http_errors.auth_error.signup_disabled.company_not_using_tabula.description",
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>

                {/* CTA Section */}
                <CardFooter className="flex-col gap-4 pt-6 border-t bg-slate-50/50">
                  <div className="w-full space-y-4">
                    <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                      {t("http_errors.auth_error.signup_disabled.need_help")}
                    </h3>
                    <p className="text-slate-600 text-sm">
                      {t(
                        "http_errors.auth_error.signup_disabled.contact_support",
                      )}
                    </p>

                    {/* CTA Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                      <Button asChild size="lg">
                        <a
                          href="https://docs.google.com/forms/d/e/1FAIpQLSdJiYSMbwRNqaIYMgNXRsTyZLV71fy69cjmCnD_RV2RMaPlDA/viewform"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <Calendar className="mr-2" />
                          {t(
                            "http_errors.auth_error.signup_disabled.cta_book_meeting",
                          )}
                        </a>
                      </Button>

                      <Button asChild variant="outline" size="lg">
                        <a href="mailto:support@tabula.de">
                          <Mail className="mr-2" />
                          {t(
                            "http_errors.auth_error.signup_disabled.cta_contact_support",
                          )}
                        </a>
                      </Button>
                    </div>
                  </div>
                </CardFooter>
              </Card>
            </div>
          </div>
        )}
        {error !== AuthError.signupDisabled && (
          <div className="h-full py-12 px-4 sm:px-6 lg:px-8">
            <div className="w-full max-w-2xl mx-auto space-y-4">
              <Button asChild variant="ghost">
                <Link to="/">
                  <ArrowLeftIcon className="mr-2" />
                  {t("http_errors.back_to_home")}
                </Link>
              </Button>
              <Card className="border-0">
                <CardHeader className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="p-3 bg-red-100 rounded-full">
                      <AlertCircle className="w-6 h-6 text-red-600" />
                    </div>
                    <CardTitle className="text-2xl font-bold text-slate-900">
                      {t("http_errors.auth_error.label")}
                    </CardTitle>
                  </div>
                  <p className="text-slate-600 text-base leading-relaxed">
                    {t("http_errors.auth_error.description")}
                  </p>
                </CardHeader>

                <CardFooter className="flex flex-col gap-4 pt-6 border-t bg-slate-50/50">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button asChild size="lg">
                      <a href="mailto:support@tabula.de">
                        <Mail className="mr-2" />
                        {t(
                          "http_errors.auth_error.signup_disabled.cta_contact_support",
                        )}
                      </a>
                    </Button>
                  </div>
                </CardFooter>
              </Card>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};
